import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetSupplierItemsApi } from './use-get-supplier-items-api';

const endpoint = 'catalog/v1/legacy-products';
export const createSupplierItemEndpointIdentifier = endpoint;

interface BodyType {
  restaurantId: number;
  supplierId: number;
  subEntityId: number;
  subEntityType: string;
  images: string[];
  name: {
    en: string;
    ar: string | null;
  };
  brand: {
    en: string | null;
    ar: string | null;
  };
  code: string;
  kasoId: string | null;
  unit: string;
  price: number;
  description: string | null;
  synonyms: string[];
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

function constructEndpoint() {
  return `${endpoint}`;
}

export function useCreateSupplierItemApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const fullEndpoint = constructEndpoint();

  const { refetch: refetchSupplierItems } = useGetSupplierItemsApi({
    enabled: false,
  });

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.post(fullEndpoint, body);

      // Refetch supplier items
      refetchSupplierItems();

      toast({
        description: 'New item has been created successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: fullEndpoint, ...options });
}
